import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computeBattleTooltipPlacement } from '../utils/battleTooltipViewportPlacement';
import '../battleStatTooltip.css';

/** Above deploy-drag-overlay (10000001) and global [data-tooltip] pseudo-elements. */
const BATTLE_STAT_TOOLTIP_Z_INDEX = 10000002;

/**
 * @typedef {'above' | 'below'} BattlePortaledTooltipPlacement
 */

/**
 * Fixed-position tooltip portaled to document.body (escapes HUD / Strategy scale transforms).
 */
export function useBattleStatTooltip() {
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);
  const [placement, setPlacement] = useState({ left: 0, top: 0, ready: false });

  /**
   * @param {React.MouseEvent<HTMLElement>} event
   * @param {string|null|undefined} text
   * @param {{ placement?: BattlePortaledTooltipPlacement, multiline?: boolean }} [options]
   */
  const showStatTooltip = useCallback((event, text, options = {}) => {
    if (text == null || text === '') return;
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      text: String(text),
      anchorRect: rect,
      preferBottom: options.placement === 'below',
      multiline: options.multiline === true,
    });
    setPlacement({ left: 0, top: 0, ready: false });
  }, []);

  const hideStatTooltip = useCallback(() => {
    setTooltip(null);
    setPlacement({ left: 0, top: 0, ready: false });
  }, []);

  const reposition = useCallback(() => {
    const tip = tooltipRef.current;
    if (!tooltip || !tip) return;

    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;
    if (!tipWidth || !tipHeight) return;

    const next = computeBattleTooltipPlacement(tooltip.anchorRect, tipWidth, tipHeight, {
      preferBottom: tooltip.preferBottom,
    });
    setPlacement({ left: next.left, top: next.top, ready: true });
  }, [tooltip]);

  useLayoutEffect(() => {
    if (!tooltip) return undefined;

    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [tooltip, reposition]);

  useLayoutEffect(() => {
    if (!tooltip) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(reposition));
    return () => cancelAnimationFrame(raf);
  }, [tooltip, reposition]);

  const statTooltipPortal =
    tooltip && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={tooltipRef}
            className={[
              'coda-battle-tooltip',
              'battle-stat-tooltip-portal',
              tooltip.multiline ? 'coda-battle-tooltip--multiline' : '',
              tooltip.multiline ? '' : 'coda-battle-tooltip--centered',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              position: 'fixed',
              left: `${placement.left}px`,
              top: `${placement.top}px`,
              opacity: placement.ready ? 1 : 0,
              zIndex: BATTLE_STAT_TOOLTIP_Z_INDEX,
            }}
            aria-hidden="true"
          >
            {tooltip.text}
          </div>,
          document.body
        )
      : null;

  return { showStatTooltip, hideStatTooltip, statTooltipPortal };
}
